import { useContext, useState } from 'react';
import { useField } from 'formik';
import type { FieldHookConfig } from 'formik';
import isEmpty from 'lodash/isEmpty';

import { useComponentDidUpdate } from '../../hooks';
import { FormContext, isRequiredField } from './utils';
import StringInput from './StringInput';
import CheckboxInput from './CheckboxInput';
import type { StringInputProps } from './StringInput';

/**
 * Пара «поле гос.номера + чекбокс "Не РФ"». Formik-обёртка поверх StringInput + CheckboxInput.
 *
 * Внутренний валидатор `validateNumber` дублирует Yup-схему vehicles — известный
 * техдолг (CONTEXT.md п. 5.10), миграция типов поведение не меняет.
 */
export interface VehicleNumberInputProps extends StringInputProps {
  /** Имя чекбокса «Не РФ». @default 'foreign' */
  nameCheckbox?: string;
  /** Инлайновая раскладка подписи чекбокса. @default false */
  labelStyle?: boolean;
}

const VehicleNumberInput = (props: VehicleNumberInputProps) => {
  const [error, setError] = useState(undefined);
  const { validationSchema, setFieldValue, values } = useContext(FormContext) as {
    validationSchema?: any;
    setFieldValue: any;
    values: any;
  };
  const {
    required,
    // onComplete,
    onValidateFail, name = 'number', nameCheckbox = 'foreign', labelStyle = false, ...rest
  } = props;

  const validateNumber = (number = '') => {
    if ([null, undefined, ''].indexOf(number) !== -1) {
      return null;
    }

    const bNumber = number.match(/^[АВЕКМНОРСТУХ]{1,2}\s?\d{3,4}\s?[АВЕКМНОРСТУХ]{0,2}\s?\d{2,3}$/i);
    const badSymbol = number.split('').reduce(function(sum, current) {
      if (!'АВЕКМНОРСТУХ0123456789'.includes(current)) return sum + 1;
      return sum;
    }, 0);

    if ((!values.foreign) && (!number || !bNumber) && number.length >7 ) {
      return 'Ошибка ввода гос.номера';
    }
    if ((!values.foreign) && (!number || badSymbol>0)) {
      return 'Некорректный символ в номере';
    }

    return null;
  };

  const [field, meta] = useField({
    // validate: validateNumber,
    ...props,
  } as FieldHookConfig<any>);

  useComponentDidUpdate(() => {
    if (!isEmpty(meta.error) && meta.touched) {
      setError(meta.error);

      if (typeof onValidateFail === 'function') {
        onValidateFail(meta.error);
      }
    } else {
      setError(undefined);
    }
  }, [meta.error, meta.touched]);

  return (
    <div className='d-flex flex-row'>
      <StringInput
        { ...rest }
        name={ name }
        placeholder='Гос.номер'
        onChange={ e => {
          setFieldValue('number', e.target.value.toUpperCase());
          setError(validateNumber(e.target.value.toUpperCase()));
        } }
        maxLength={ 20 }
        rootClassName='flex-grow-1 mr-2'
        required={ required || isRequiredField(validationSchema, field.name) }
        error={ error }
      />
      <CheckboxInput
        name={ nameCheckbox }
        label='Не РФ'
        rootClassName={ labelStyle ? 'mb-0 mt-4 pt-3' : 'mt-1 pt-1' }
        labelClassName='text-nowrap'
        disabled={ rest?.disabled }
      />
    </div>
  );
};

export default VehicleNumberInput;
